// What a board, part or asset sticker is, as a card. The home routes drones and boxes
// to their own screens; everything else lands here. describeSticker does the reading.

import React from 'react';
import { Link } from 'react-router-dom';
import { FOCUS, MOTION } from '../../Requisitions/buttonStyles';
import { scanDestination, type ScanLookupResult } from '../../utils/scanDestination';
import { routeAllowedOnInstance } from '../../navigation/routeInstances';
import { inShell } from '../shell';
import { OpenIcon, WarningIcon } from '../icons';
import type { StickerReading } from './types';

const OpenItsPage: React.FC<{ result: ScanLookupResult }> = ({ result }) => {
  const to = scanDestination(result);
  if (!to || !routeAllowedOnInstance(to)) return null;
  return (
    <Link
      to={to}
      className={`mt-3 -mb-1 -ml-2 inline-flex min-h-[2.75rem] items-center gap-1 rounded-full px-2 text-sm font-medium text-on-surface-variant hover:text-on-surface ${FOCUS} ${MOTION}`}
    >
      Open its page
      <OpenIcon className="h-4 w-4" />
    </Link>
  );
};

const StickerCard: React.FC<{ reading: StickerReading; result: ScanLookupResult | null }> = ({ reading, result }) => (
  <section aria-label={`${reading.kind} ${reading.name}`} className="rounded-xl bg-surface-container p-4 motion-safe:animate-rowLand">
    <p className="text-sm text-on-surface-variant">{reading.kind}</p>
    <h2
      className={`mt-0.5 text-on-surface [text-wrap:balance] ${
        reading.nameIsCode ? 'font-mono text-2xl font-semibold tracking-tight break-all' : 'text-2xl font-semibold'
      }`}
    >
      {reading.name}
    </h2>
    {reading.subtitle && <p className="mt-0.5 text-base text-on-surface-variant break-words">{reading.subtitle}</p>}

    {reading.facts.length > 0 && (
      <dl className="mt-4 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-base">
        {reading.facts.map((f) => (
          <React.Fragment key={f.label}>
            <dt className="text-on-surface-variant">{f.label}</dt>
            <dd className="min-w-0 break-words text-on-surface">{f.value}</dd>
          </React.Fragment>
        ))}
      </dl>
    )}

    {reading.warning && (
      <p className="mt-4 flex gap-2 rounded-lg bg-amber-900/40 px-3 py-2.5 text-sm leading-snug text-amber-200">
        <WarningIcon className="mt-px h-4 w-4 shrink-0" />
        <span>{reading.warning}</span>
      </p>
    )}

    {/* Browsers only: the kiosk renders no router and has no pages to open. */}
    {result && !inShell() && <OpenItsPage result={result} />}
  </section>
);

export default StickerCard;
